import { Fragment, useContext, useState, useEffect, useRef } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import 'dayjs/locale/en';
import calendar from 'dayjs/plugin/calendar';
import { Row, Container, Button } from 'react-bootstrap';
import { Flex, Box } from 'theme-ui';
import { IconButton } from '@velove/design-system';
import { useUpdateDeliveryWindow } from './hooks/useUpdateDeliveryWindow';
import { DeliveryStatusType } from '../../contexts/contextTypes';

const PendingContainer = ({ isCustomerService }: { isCustomerService: boolean }) => {
  const { packageId } = useParams();
  const { t, i18n } = useTranslation();
  const { trackingDetails } = useContext(AppContext);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedWindow, setSelectedWindow] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDeliveryWindowMutation = useUpdateDeliveryWindow();

  dayjs.extend(calendar);
  dayjs.locale(i18n.language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleReschedule = async () => {
    if (selectedWindow && packageId && trackingDetails?.task?.id) {
      setErrorMessage('');

      try {
        const window = JSON.parse(selectedWindow);

        await updateDeliveryWindowMutation.mutateAsync({
          packageId,
          payload: {
            taskId: trackingDetails.task.id,
            deliveryWindow: {
              start: new Date(window.start).toISOString(),
              end: new Date(window.end).toISOString()
            }
          }
        });

        // Success: close editing mode and refresh the tracking details
        setIsEditing(false);
        setSelectedWindow('');
        setIsDropdownOpen(false);

        // Optionally show a success message to the user
        console.log('Delivery window successfully updated');
      } catch (error: any) {
        // Handle error
        const errorMsg = error?.response?.data?.message || 'Failed to update delivery window. Please try again.';
        setErrorMessage(errorMsg);
        console.error('Error updating delivery window:', errorMsg);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedWindow('');
    setIsDropdownOpen(false);
    setErrorMessage('');
  };

  const getCurrentEtaValue = () => {
    if (trackingDetails?.eta?.lowerBound && trackingDetails?.eta?.upperBound) {
      return JSON.stringify({
        start: trackingDetails.eta.lowerBound,
        end: trackingDetails.eta.upperBound
      });
    }
    return '';
  };

  const formatDeliveryDate = (date: Date) => {
    const etaDate = dayjs(date);
    const today = dayjs();

    if (etaDate.isSame(today, 'day')) {
      return `${t('delivery_today')}, `;
    } else if (etaDate.isSame(today.subtract(1, 'day'), 'day')) {
      return `${t('delivery_yesterday')}, `;
    } else if (etaDate.isSame(today.add(1, 'day'), 'day')) {
      return `${t('delivery_tomorrow')}, `;
    } else if (etaDate.isBefore(today, 'day')) {
      return `${etaDate.format('YYYY-MM-DD')}, `;
    } else {
      return `${etaDate.format('dddd')}, `;
    }
  };

  const formatDeliveryWindow = (window: { start: Date; end: Date }) => {
    const startTime = dayjs(window.start).format('HH:mm');
    const endTime = dayjs(window.end).format('HH:mm');
    const date = formatDeliveryDate(window.start);
    return `${date}${startTime} - ${endTime}`;
  };

  const shouldShowRescheduleButton = () => {
    // Check basic conditions first
    if (isCustomerService) return false;
    if (!trackingDetails?.task?.completeAfter) return false;
    if (!trackingDetails?.task?.completeBefore) return false;
    if (trackingDetails?.task?.status !== DeliveryStatusType.PENDING) return false;

    const currentTime = dayjs();
    const bookingDate = dayjs(trackingDetails?.task.completeBefore);
    
    // Create cutoff datetime by combining booking date with 14:30
    const cutoffDateTime = bookingDate.hour(14).minute(30).second(0).millisecond(0);
    
    // Return true if current time is before the cutoff, false otherwise
    return currentTime.isBefore(cutoffDateTime);
  };

  return (
    <Fragment>
      {trackingDetails?.announcement && (
        <Flex sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px' }}>
          <Box className="announcement-box">
            <span>⚠️</span>
            <span className="blink-text">{trackingDetails?.announcement}</span>
          </Box>
        </Flex>
      )}
      <Container>
        <Row className="home_container">
          <div className="summary_container">
            <div className="summary_container--header">
              <p style={{ marginBottom: 10 }}>{t('tracking_number')}</p>
              <h3 style={{ marginBottom: 40 }}>{trackingDetails?.package?.externalPackageId}</h3>
            </div>

            <p style={{ marginBottom: 10 }}>{t('delivery_time')}</p>
            {trackingDetails?.eta?.lowerBound && trackingDetails?.eta?.upperBound ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: errorMessage ? 10 : 40 }}>
                  {!isEditing ? (
                    <>
                      <h3
                        id="eta-display"
                        className="eta-display-text"
                        style={{
                          marginBottom: 0
                        }}
                      >
                        {formatDeliveryDate(trackingDetails?.eta?.lowerBound || new Date())}
                        {dayjs(trackingDetails?.eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                          ' - ' +
                          dayjs(trackingDetails?.eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
                      </h3>
                      {shouldShowRescheduleButton() && trackingDetails?.deliveryWindows && trackingDetails.deliveryWindows.length > 0 && (
                        <IconButton
                          id="reschedule-button"
                          icon={'Edit'}
                          sx={{
                            height: '40px',
                            width: '40px',
                            padding: '4px',
                            color: 'white',
                            borderRadius: '100%',
                            borderStyle: 'solid',
                            borderWidth: '2px',
                            backgroundColor: 'var(--container-bg-color)',
                            borderColor: 'white'
                          }}
                          onClick={() => {
                            setIsEditing(true);
                            setSelectedWindow(getCurrentEtaValue());
                            setErrorMessage('');
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div ref={dropdownRef} className="custom-dropdown-wrapper">
                        <div id="eta-display" className="custom-dropdown-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          <span>
                            {selectedWindow
                              ? (() => {
                                  const window = JSON.parse(selectedWindow);
                                  return formatDeliveryWindow(window);
                                })()
                              : formatDeliveryDate(trackingDetails?.eta?.lowerBound || new Date()) +
                                dayjs(trackingDetails?.eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                                ' - ' +
                                dayjs(trackingDetails?.eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
                          </span>
                          <span style={{ marginLeft: '8px' }}>▼</span>
                        </div>
                        {isDropdownOpen && (
                          <div className="custom-dropdown-menu">
                            <div
                              className={`custom-dropdown-item ${selectedWindow === getCurrentEtaValue() ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedWindow(getCurrentEtaValue());
                                setIsDropdownOpen(false);
                              }}
                            >
                              {formatDeliveryDate(trackingDetails?.eta?.lowerBound || new Date())}
                              {dayjs(trackingDetails?.eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                                ' - ' +
                                dayjs(trackingDetails?.eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
                            </div>
                            {trackingDetails?.deliveryWindows?.map((window, index) => {
                              const windowValue = JSON.stringify(window);
                              return (
                                <div
                                  key={index}
                                  className={`custom-dropdown-item ${selectedWindow === windowValue ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedWindow(windowValue);
                                    setIsDropdownOpen(false);
                                  }}
                                >
                                  {formatDeliveryWindow(window)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <Button
                        id="confirm-reschedule-button"
                        className="reschedule-confirm-button"
                        onClick={handleReschedule}
                        disabled={!selectedWindow || selectedWindow === getCurrentEtaValue() || updateDeliveryWindowMutation.isLoading}
                      >
                        {updateDeliveryWindowMutation.isLoading ? '⏳' : '✓'}
                      </Button>
                      <Button id="cancel-reschedule-button" className="reschedule-cancel-button" onClick={handleCancelEdit}>
                        ✕
                      </Button>
                    </>
                  )}
                </div>
                {errorMessage && (
                  <div
                    style={{
                      padding: '10px',
                      marginBottom: '30px',
                      backgroundColor: '#fee',
                      border: '1px solid #fcc',
                      borderRadius: '4px',
                      color: '#c33'
                    }}
                  >
                    {errorMessage}
                  </div>
                )}
              </>
            ) : (
              <h3
                style={{
                  marginBottom: 40,
                  fontSize: '18px'
                }}
              >
                {t('eta.eta_error')}
              </h3>
            )}
            <p>{t('delivery_summary')}</p>
          </div>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PendingContainer;
